import React from "react";
import { useAuth, useRoles, useQualification } from "../api";
import useAppSettings from "./appSettings";
import { Permission, usePermissions } from "./permissions";
import type { UserWithRole } from "../../api/auth/Auth.typed";
import type { QualificationItem } from "../../api/qualification/Qualification.typed";

export type UsersModuleDataContextValue = {
  users: UserWithRole[] | undefined;
  refreshUsers: () => Promise<void>;
  permissionsCatalog: ReturnType<typeof useRoles>["permissionsCatalog"];
  loadPermissionsCatalog: () => Promise<void>;
  qualificationItems: QualificationItem[];
  loading: boolean;
  rolesReady: boolean;
  isDesync: boolean;
};

const UsersModuleDataContext = React.createContext<UsersModuleDataContextValue | undefined>(
  undefined,
);

export function UsersModuleDataProvider({ children }: { children: React.ReactNode }) {
  const { getUsers, user } = useAuth();
  const { permissionsCatalog, loadPermissionsCatalog } = useRoles();
  const { listQualificationItems } = useQualification();
  const { config } = useAppSettings();
  const { hasPermission } = usePermissions();

  const [users, setUsers] = React.useState<UserWithRole[] | undefined>();
  const [qualificationItems, setQualificationItems] = React.useState<QualificationItem[]>([]);
  const [loading, setLoading] = React.useState(false);

  const companyId = user?.Role?.company_id ?? user?.company_id ?? 0;
  const isDesync = config?.CustomersSync === false;

  const canAccessUsersModuleData =
    hasPermission(Permission.USERS_ACCESS) &&
    (hasPermission(Permission.USERS_ALL_VIEW) ||
      hasPermission(Permission.USERS_COMPANY_VIEW) ||
      hasPermission(Permission.USERS_OWN_VIEW) ||
      hasPermission(Permission.USERS_OWN_EDIT));

  const needsPermissionsCatalog =
    hasPermission(Permission.USERS_ACCESS) &&
    (hasPermission(Permission.USERS_ALL_VIEW) ||
      hasPermission(Permission.USERS_ALL_EDIT) ||
      hasPermission(Permission.USERS_COMPANY_VIEW) ||
      hasPermission(Permission.USERS_COMPANY_EDIT) ||
      hasPermission(Permission.USERS_ROLES_VIEW) ||
      hasPermission(Permission.USERS_ROLES_EDIT));

  const rolesReady =
    !needsPermissionsCatalog || (permissionsCatalog?.allPermissions?.length ?? 0) > 0;

  const refreshUsers = React.useCallback(async () => {
    const us = await getUsers();
    setUsers(us);
  }, [getUsers]);

  React.useEffect(() => {
    if (companyId <= 0) return;
    setLoading(true);
    const tasks: Promise<unknown>[] = [];
    if (needsPermissionsCatalog) {
      tasks.push(loadPermissionsCatalog());
    }
    if (
      hasPermission(Permission.USERS_ACCESS) &&
      (hasPermission(Permission.USERS_ALL_VIEW) ||
        hasPermission(Permission.USERS_COMPANY_VIEW) ||
        hasPermission(Permission.USERS_OWN_VIEW) ||
        hasPermission(Permission.USERS_OWN_EDIT))
    ) {
      tasks.push(refreshUsers());
    } else {
      setUsers(undefined);
    }
    if (tasks.length === 0) {
      setLoading(false);

      return;
    }
    Promise.all(tasks).finally(() => setLoading(false));
  }, [companyId, hasPermission, loadPermissionsCatalog, needsPermissionsCatalog, refreshUsers]);

  React.useEffect(() => {
    if (companyId <= 0 || !canAccessUsersModuleData) {
      setQualificationItems([]);

      return;
    }

    listQualificationItems().then((items) => setQualificationItems(items ?? []));
  }, [companyId, canAccessUsersModuleData, listQualificationItems]);

  const value = React.useMemo(
    () => ({
      users,
      refreshUsers,
      permissionsCatalog,
      loadPermissionsCatalog,
      qualificationItems,
      loading,
      rolesReady,
      isDesync,
    }),
    [
      users,
      refreshUsers,
      permissionsCatalog,
      loadPermissionsCatalog,
      qualificationItems,
      loading,
      rolesReady,
      isDesync,
    ],
  );

  return (
    <UsersModuleDataContext.Provider value={value}>{children}</UsersModuleDataContext.Provider>
  );
}

export function useUsersModuleData(): UsersModuleDataContextValue {
  const ctx = React.useContext(UsersModuleDataContext);
  if (!ctx) throw new Error("useUsersModuleData must be used within UsersModuleDataProvider");
  return ctx;
}
